'use strict';

import sha256 from 'js-sha256';
import {size, isEmpty} from 'underscore';

import TermUtils from '@uw-it-sis/lib-react/lib/TermUtils';

/**
 * Given a base url, a term id, a list of SLNs, and a map of SLNs to add codes ( or an empty string if none available ) ,
 * return a URL suitable for navigation to the SPS registration page populated with the given data as input
 *
 *
 * If the term id cannot be determined to be valid, or if some other problem occurs, an empty string will be returned.
 *
 * @param baseUrl the protocol and server portion of the url
 * @param systemId the student system id of the user
 * @param termId the term id
 * @param slnList list of SLNs to build parameters
 * @param slnsToAddCodes mapping of SLNs to add codes that should be added as parameters.  May be empty.
 * @param src string to pass to register.asp to know the source of where the reg url is coming from.
 *
 * @return string url suitable for opening a new tab to SPS, or empty if invalid data was detected
 */
export function buildRegistrationUrl(baseUrl, systemId, termId, slnList, slnsToAddCodes, src = 'mp_reg_preview') {
    let encodedSystemId = sha256(systemId);

    let result = '';

    let termObject = TermUtils.buildTermObject(termId);

    let urlSuffix = '';

    let haveAddCodesToCheck = size(slnsToAddCodes) > 0;

    if (termObject !== null && !isEmpty(slnList)) {
        for (let i = 0; i < slnList.length; ++i) {

            let sln = slnList[i];

            let addCode = '';
            if (haveAddCodesToCheck && !isEmpty(slnsToAddCodes[sln])) {
                addCode = slnsToAddCodes[sln];
            }

            // parameters are indexed starting at 1, so add 1 to the 0-based array index
            let paramIndex = i + 1;
            urlSuffix += `&sln${paramIndex}=${sln}`;
            urlSuffix += `&entCode${paramIndex}=${addCode}`;
            urlSuffix += `&credits${paramIndex}=&gr_sys${paramIndex}=`;
        }

        let fullUrl = `${baseUrl}/students/UWNetID/register.asp?INPUTFORM=UPDATE&PAC=0&MAXDROPS=0&_CW=${encodedSystemId}`;
        fullUrl += `&_src=${src}&QTR=${termObject.termNumber}&YR=${termObject.termYear}`;
        fullUrl += urlSuffix;

        result = fullUrl;

    }

    return result;
}

/**
 * Build a list of SLNs discovered from the given list of ready registration items.
 *
 * The input is expected to be in the structure of this java object:
 * org.kuali.student.myplan.plan.dataobject.PlannedCourseDataObject in myplan-ui
 *
 * The SLNs returned will NOT have any children of a block registration parent found in the list.  Most of the work
 * of this method is to discover those relationships and parse out which parents and children are included in the list.
 *
 * @param readyList list of course data objects
 *
 * @return Array list of SLN strings
 */
export function buildRegistrationSlnList(readyList) {
    if ( size(readyList) === 0 ) {
        return [];
    }

    // gather a mapping of all the SLNs to a map with an add code and the block reg parent SLN(s), if either exist
    let slnMap = {};
    let slns = [];
    readyList.map(function(course, i) {

        let activities = course['planActivities'];
        activities.map(function(activity, j) {
            let sln = activity['registrationCode'];

            let blockRegParents = [];

            let blockRegDefinition = activity['blockRegistration'];

            if (!isEmpty(blockRegDefinition) && !blockRegDefinition['parent']) {
                blockRegParents = blockRegDefinition['parentSlns'];
            }

            slns.push(sln);
            slnMap[sln] = {
                blockRegParents: blockRegParents
            };
        });
    });

    // If a section that is a child of a block reg group has
    // a parent in the list of all SLNs to be built, then the child will not be built in the list,
    // as the registration endpoint will handle populating children of a block reg
    let slnsToSend = [];

    slns.map(function (sln, i) {
        let relatedData = slnMap[sln];

        let blockRegParents = relatedData['blockRegParents'];

        let hasBlock = false;
        let shouldAddChild = false;

        if ( size(blockRegParents) > 0 ) {

            hasBlock = true;

            let mapContainsParent = false;
            blockRegParents.map(function(parentSln, j) {

                if ( !mapContainsParent && slns.includes(parentSln) ) {
                    mapContainsParent = true;
                }
            });

            shouldAddChild = !mapContainsParent;
        }

        if ( !hasBlock || shouldAddChild ) {
            slnsToSend.push(sln);
        }

    });

    return slnsToSend;
}